import React, { useEffect, useState } from "react";
import ReactModal from "react-modal";
import Image from "next/image";
import TrashIcon from "./trashIcon.svg";
import { useCookies } from "react-cookie";
import { useRouter } from "next13-progressbar";
import { isBusinessOpen } from "../notAcceptingOrdersBanner/notAcceptingOrdersBanner";
import { PrimaryButton } from "../button/primaryButton";
import { getTimezoneAbbreviation } from "@/utils/timeZoneAbbreviation";
import { DateTime } from "luxon";
import { SuccessBanner, UnavailableBanner } from "./cartStatusBanner";
import { usePathname } from "next/navigation";
import CloseIcon from "@/components/_icons/closeIcon";
import { OrderType } from "@/types";
import { OrderTypeDropdown } from "../orderTypeDropdown/orderTypeDropdown";
import {
  ScheduledDateDropdown,
  ScheduleDateType,
} from "../scheduledDateDropdown/scheduledDateDropdown";
import {
  ScheduledTimeDropdown,
  ScheduleTimeType,
} from "../scheduledTimeDropdown/scheduledTimeDropdown";
import { AgeRestricted } from "../ageRestricted/ageRestricted";
import ArrowLeftIcon from "@/components/_icons/arrowLeftIcon";

export type Modifier = {
  name?: string;
  sortOrder?: number;
  available?: boolean;
  id?: string;
  price?: number;
  groupId?: string;
};

export type CartItem = {
  key: string;
  value: {
    id: string;
    name: string;
    price: number;
    quantity: number;
    modifiers: Modifier[];
    subtotal: number;
    total: number;
    cartId: string;
    specialInstructions: string;
  };
};

const extractHashFromUrl = (url: string) => {
  const urlObj = new URL(url);
  return urlObj.hash;
};

export const CartDrawer = ({
  isOpen,
  onClose,
  onRequestClose,
  merchant,
  onTriggerLoginModal,
  retailPickupTime,
  setDeliveryAddressModalOpen,
  setIsCartOpen,
}: any) => {
  const hash =
    typeof window !== "undefined"
      ? extractHashFromUrl(window?.location?.href)
      : "";
  const pathName = usePathname();
  const merchantMeta = merchant.merchantMeta;
  const merchantColor = merchantMeta.colorPalette?.color;
  const isHybridMerchant = merchant.isHybrid;
  const now = new Date();
  const [cookies] = useCookies(["session"]);
  const router = useRouter();
  const [cartItems, setCartItems] = useState(() => {
    if (typeof window !== "undefined") {
      const storedCart = localStorage.getItem("cart");
      return storedCart ? JSON.parse(storedCart) : [];
    } else {
      return [];
    }
  });
  const [orderType, setOrderType] = useState<OrderType>(() => {
    if (typeof window !== "undefined") {
      const sessionTime = JSON.parse(
        localStorage.getItem("scheduledTime") || "{}"
      );
      return sessionTime?.orderType || "pickup";
    }
    return "pickup";
  });
  const [showScheduledOrderForm, setShowScheduledOrderForm] = useState(false);
  const [scheduleDate, setScheduleDate] = useState<ScheduleDateType>({
    displayValue: "Today",
    dateObject: new Date(),
  });
  const [scheduleTime, setScheduleTime] = useState<ScheduleTimeType>({
    displayValue: "",
    timeObject: new Date(now),
  });
  const businessHours = merchant?.businessHours?.hours;
  const allDaysEmpty =
    businessHours &&
    Object.values(businessHours).every((day: any) => day.length === 0);
  let checkoutDisabled =
    cartItems.length == 0 ||
    !isBusinessOpen(merchant) ||
    !businessHours ||
    allDaysEmpty ||
    Object.keys(retailPickupTime).length === 0;

  const itemsCount = cartItems.reduce(
    (adder: any, item: any) => adder + item.value?.quantity,
    0
  );

  useEffect(() => {
    const scheduledTimeString =
      global?.window?.localStorage.getItem("scheduledTime");
    const sessionTime = scheduledTimeString
      ? JSON.parse(scheduledTimeString)
      : {};

    if (merchant.isRetail) {
      global?.window?.localStorage.setItem(
        "scheduledTime",
        JSON.stringify({
          orderType: orderType,
        })
      );
    }
    if (
      orderType === "delivery" &&
      !JSON.parse(
        global?.window?.localStorage.getItem("deliveryAddress") || "{}"
      )?.address1
    ) {
      setDeliveryAddressModalOpen(true);
      setIsCartOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderType]);

  useEffect(() => {
    if (isOpen && businessHours) {
      const pickupTimeData = {
        epoch: retailPickupTime?.pickupDateTimeMillis,
        type: "scheduled",
      };
      global?.window?.sessionStorage.remove;
      global?.window?.sessionStorage.setItem(
        "pickupTime",
        JSON.stringify(pickupTimeData)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, businessHours, merchantMeta.leadTime, merchantMeta.timezone]);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "cart") {
        const updatedCart = event.newValue ? JSON.parse(event.newValue) : [];
        setIsCartOpen(true);
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isOpen && typeof window !== "undefined") {
      const storedCart = localStorage.getItem("cart");
      if (storedCart) {
        setCartItems(JSON.parse(storedCart));
      }
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const removeFromCart = (index: any) => {
    setCartItems((prevCartItems: any[]) =>
      prevCartItems.filter((_: any, i: any) => i !== index)
    );
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("cart", JSON.stringify(cartItems));
    }
  }, [cartItems]);

  const handleBackToCart = () => {
    setShowScheduledOrderForm(false);
  };

  const handleConfirmScheduledOrder = () => {
    localStorage.setItem(
      "scheduledTime",
      JSON.stringify({
        orderType: orderType,
        date: scheduleDate,
        time: scheduleTime,
      })
    );

    setShowScheduledOrderForm(false);
  };

  const handleContinueToCheckout = () => {
    if (cookies.session) {
      router.push("/checkout");
    } else {
      onTriggerLoginModal(true);
    }
  };

  const pickupTimeDisplay = formatPickupTime(
    retailPickupTime?.pickupDateTimeMillis,
    merchant.timezone,
    merchant.locale
  );

  return (
    <ReactModal
      ariaHideApp={false}
      appElement={global?.window?.document?.getElementById("app") || undefined}
      isOpen={isOpen}
      onRequestClose={() => {
        setShowScheduledOrderForm(false);
        if (hash !== undefined) {
          window.history.replaceState({}, "", pathName);
        }
        onRequestClose();
      }}
      className="fixed top-0 right-0 bottom-0 sm:w-[400px] bg-white shadow-md focus:outline-none flex flex-col w-full"
      overlayClassName="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50"
      shouldCloseOnOverlayClick={true}
      style={{
        overlay: {
          zIndex: 1000,
        },
      }}
    >
      <div className="flex flex-col h-full w-[100%]">
        {showScheduledOrderForm ? (
          <div className="flex flex-col h-full p-4">
            <div className="pt-4 flex flex-row gap-2">
              <button
                onClick={() => {
                  handleBackToCart();
                }}
                className="text-gray-600 hover:text-gray-900"
              >
                <ArrowLeftIcon />
              </button>
              <h2 className="text-xl font-bold">Schedule Your Order</h2>
            </div>
            <div className="py-4">
              <ScheduledDateDropdown
                scheduleDate={scheduleDate}
                setScheduleDate={setScheduleDate}
                scheduleDateRange={3}
                setScheduleTime={setScheduleTime}
                businessHours={businessHours}
                timezone={merchant.timezone}
              />
            </div>
            {businessHours && !allDaysEmpty && (
              <div className="py-4">
                <ScheduledTimeDropdown
                  scheduleTime={scheduleTime}
                  setScheduleTime={setScheduleTime}
                  businessHours={businessHours}
                  scheduleDate={scheduleDate}
                  leadTime={merchant.leadTime}
                  timezone={merchant.timezone}
                  orderType={orderType}
                />
              </div>
            )}
            <div className="mt-auto pb-4 flex gap-4">
              <button
                className="bg-black text-white font-bold py-2 rounded-md w-full h-14 disabled:bg-[#C7C7C7]"
                onClick={handleConfirmScheduledOrder}
                disabled={scheduleTime.displayValue == ""}
              >
                Confirm
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex flex-row justify-between items-start p-4 py-8">
              <div>
                <h2 className="text-2xl font-bold">
                  {isHybridMerchant && !cartItems.length
                    ? "Appointment Summary"
                    : `Your Cart (${itemsCount})`}
                </h2>
              </div>

              <button
                onClick={onClose}
                className="text-gray-600 hover:text-gray-900 mr-4"
              >
                <CloseIcon />
              </button>
            </div>
            <div className="px-4 pb-4 flex flex-row justify-between items-center gap-4">
              <OrderTypeDropdown
                orderType={orderType}
                setOrderType={setOrderType}
                availableOrderTypes={merchant.services}
              />
              {businessHours && !allDaysEmpty && (
                <div className="w-[70%] flex flex-col pb-[22px]">
                  <p className="text-[12px] text-[#727272] pb-2">For when?</p>
                  {retailPickupTime && <>{pickupTimeDisplay}</>}
                </div>
              )}
            </div>
            {hash && hash === "#cart" && <SuccessBanner />}
            {hash && hash === "#cart-unavailable" && (
              <UnavailableBanner count={cartItems.length} />
            )}

            <div className="flex-grow overflow-y-auto p-4">
              {cartItems.map((item: any, index: any) => (
                <div
                  key={index}
                  className="flex flex-col justify-between items-center py-4 border-b"
                >
                  <div className="w-full flex flex-row justify-between">
                    <div className="w-full flex flex-col justify-between">
                      <p className="text-lg text-[#373737] max-w-[310px]">
                        {item.value?.name}{" "}
                        {item.value?.quantity > 1 &&
                          `× ` + item.value?.quantity}
                      </p>
                      {item.value?.isAgeRestricted && (
                        <AgeRestricted text="Government-issued ID required" />
                      )}
                      <div className="max-w-[80%]">
                        {item.value?.modifiers.length > 0 && (
                          <p className="text-[15px] text-[#727272]">
                            {item.value?.modifiers
                              .map((modifier: any) => modifier.name)
                              .join(", ")}
                          </p>
                        )}
                        <div className="w-full">
                          <p className="text-[15px] text-[#727272] max-w-[310px] break-words">
                            {item.value?.specialInstructions}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col justify-start items-end">
                      <p className="text-[16px] font-medium mt-0.5">
                        {Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: "USD",
                          minimumFractionDigits: 2,
                        }).format(item.value?.total / 100)}
                      </p>
                      <button
                        onClick={() => removeFromCart(index)}
                        className="mt-2"
                      >
                        <Image
                          src={TrashIcon.src}
                          height={14}
                          width={14}
                          alt="remove cart item"
                        />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-4 pb-8">
              <PrimaryButton
                style={{
                  backgroundColor: checkoutDisabled ? "#C7C7C7" : merchantColor,
                }}
                className="bg-black text-white font-bold py-2 rounded-md w-full h-14 disabled:bg-[#C7C7C7]"
                onClick={handleContinueToCheckout}
                disabled={checkoutDisabled}
              >
                <p className="text-xl font-medium">Continue to checkout</p>
              </PrimaryButton>
            </div>
          </>
        )}
      </div>
    </ReactModal>
  );
};

const formatPickupTime = (
  pickupTimeData: number,
  timezone: string,
  locale: string
) => {
  if (pickupTimeData) {
    const date = new Date(pickupTimeData);
    const formattedDate = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      timeZone: timezone,
    });
    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: timezone,
    });

    return `${formattedDate}, at ${formattedTime} ${getTimezoneAbbreviation(
      timezone,
      locale
    )}`;
  }

  return "Scheduled time not available";
};

const parseTime = (timeString: string, referenceDate: DateTime) => {
  const hours = Math.floor(Number(timeString) / 100);
  const minutes = Number(timeString) % 100;
  return referenceDate.set({
    hour: hours,
    minute: minutes,
    second: 0,
    millisecond: 0,
  });
};

const findNextBusinessDay = (
  startDayIndex: number,
  daysOfWeek: any,
  pickupHours: any
) => {
  for (let i = 1; i <= 7; i++) {
    const nextDayIndex = (startDayIndex + i) % 7;
    const nextDay = daysOfWeek[nextDayIndex];
    if (pickupHours[nextDay] && pickupHours[nextDay].length > 0) {
      return {
        dayIndex: nextDayIndex,
        hours: pickupHours[nextDay],
        daysToAdd: i,
      };
    }
  }
  return null;
};
